import { useEffect, useMemo, useState } from "react";
import JSZip from "jszip";

const toPreviewArray = (input) => {
  if (Array.isArray(input)) return input;
  if (typeof input === "string" && input.trim()) {
    const raw = input.trim();
    if (raw.startsWith("[") && raw.endsWith("]")) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed;
      } catch (e) {
        // fallback
      }
    }
    if (raw.includes(",") && !raw.startsWith("data:")) {
      return raw.split(",").map((v) => v.trim()).filter(Boolean);
    }
    return [raw];
  }
  return [];
};

const sanitizeFileName = (name = "") =>
  String(name)
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-zA-Z0-9._-]/g, "")
    .toLowerCase();

export default function FileUpload({
  value,
  onChange,
  multiple,
  maxFiles,
  maxFileSizeMB,
  accept = "image/*",
  mode = "append",
  emitHostedUrls = false,
  hostedBasePath = "/uploaded-products/manual/angles/",
  parseZipByName = false,
  hostedRoot = "/uploaded-products",
}) {
  const [preview, setPreview] = useState(() => toPreviewArray(value));
  const [error, setError] = useState("");
  const maxBytes = useMemo(() => {
    const mb = Number(maxFileSizeMB);
    return Number.isFinite(mb) && mb > 0 ? mb * 1024 * 1024 : null;
  }, [maxFileSizeMB]);

  useEffect(() => {
    const next = toPreviewArray(value);
    setPreview((prev) => {
      if (prev.length === next.length && prev.every((v, i) => v === next[i])) {
        return prev;
      }
      return next;
    });
  }, [value]);

  const handleFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setError("");

    const acceptedTypes = String(accept || "")
      .split(",")
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean);
    const isTypeAllowed = (file) => {
      if (!acceptedTypes.length || acceptedTypes.includes("*/*")) return true;
      const type = String(file.type || "").toLowerCase();
      return acceptedTypes.some((a) => {
        if (a.endsWith("/*")) return type.startsWith(a.slice(0, -1));
        return type === a;
      });
    };

    const validByType = files.filter(isTypeAllowed);
    if (validByType.length !== files.length) {
      setError("Some files were skipped due to unsupported type.");
    }

    const validBySize = validByType.filter((file) => !maxBytes || file.size <= maxBytes);
    if (validBySize.length !== validByType.length) {
      const sizeText = Number(maxFileSizeMB) || 0;
      setError(`Some files were skipped. Max size: ${sizeText} MB per file.`);
    }

    const currentPreview = mode === "append" ? toPreviewArray(preview) : [];
    let mergedFiles = validBySize;
    if (maxFiles && Number.isFinite(Number(maxFiles)) && Number(maxFiles) > 0) {
      const remaining = Math.max(0, Number(maxFiles) - currentPreview.length);
      if (remaining <= 0) {
        setError(`Maximum ${maxFiles} files allowed.`);
        e.target.value = "";
        return;
      }
      if (validBySize.length > remaining) {
        mergedFiles = validBySize.slice(0, remaining);
        setError(`Only ${remaining} file(s) added. Maximum ${maxFiles} files allowed.`);
      }
    }

    const finalize = (images) => {
      const updated = multiple ? [...currentPreview, ...images] : images.slice(0, 1);
      setPreview(updated);
      onChange(updated);
      e.target.value = "";
    };

    const finalizeStructured = (payload) => {
      setPreview([]);
      onChange(payload);
      e.target.value = "";
    };

    const parseZipStructured = async (zipFile) => {
      const zip = await JSZip.loadAsync(zipFile);
      const filesInZip = [];
      zip.forEach((relativePath, entry) => {
        if (entry.dir) return;
        filesInZip.push(relativePath.split("/").pop() || relativePath);
      });

      const colorSet = new Set();
      const variantMap = {};
      const gallery = [];
      const colorMap = {};
      let skuDetected = "";

      filesInZip.forEach((fileName) => {
        const parts = String(fileName).split(".");
        if (parts.length < 2) return;
        const ext = parts.pop().toLowerCase();
        if (!["jpg", "jpeg", "png", "webp"].includes(ext)) return;
        const base = parts.join(".");
        const match = base.match(/^([a-zA-Z0-9-]+)_([a-zA-Z0-9-]+)_([a-zA-Z0-9-]+)_(\d+)$/);
        if (!match) return;

        const sku = match[1];
        const color = match[2].toLowerCase();
        const angle = match[3].toLowerCase();
        const seq = match[4];
        if (!skuDetected) skuDetected = sku;
        const url = `${hostedRoot}/${sku}/${color}/${angle}/${sku}_${color}_${angle}_${seq}.${ext}`;

        if (!variantMap[color]) variantMap[color] = {};
        if (!variantMap[color][angle]) variantMap[color][angle] = url;
        colorSet.add(color);
        gallery.push(url);
      });

      Object.entries(variantMap).forEach(([color, shots]) => {
        colorMap[color] = shots.front || Object.values(shots)[0] || "";
      });

      const availableColors = [...colorSet].map((c) => c.charAt(0).toUpperCase() + c.slice(1));
      const normalizedVariantMap = {};
      Object.entries(variantMap).forEach(([color, shots]) => {
        const c = color.charAt(0).toUpperCase() + color.slice(1);
        normalizedVariantMap[c] = shots;
      });
      const normalizedColorMap = {};
      Object.entries(colorMap).forEach(([color, url]) => {
        const c = color.charAt(0).toUpperCase() + color.slice(1);
        normalizedColorMap[c] = url;
      });

      return {
        __structuredImport: true,
        sku: skuDetected,
        images: [...new Set(gallery)],
        availableColors: availableColors.join(", "),
        colorImageMap: normalizedColorMap,
        variantGalleryMap: normalizedVariantMap,
        productJson: JSON.stringify(
          {
            sku: skuDetected,
            availableColors,
            images: [...new Set(gallery)],
            colorImageMap: normalizedColorMap,
            variantGalleryMap: normalizedVariantMap,
          },
          null,
          2
        ),
      };
    };

    if (parseZipByName) {
      const zipFile = mergedFiles[0];
      if (!zipFile) {
        setError("Select a ZIP file.");
        e.target.value = "";
        return;
      }
      parseZipStructured(zipFile)
        .then((payload) => {
          if (!payload.images.length) {
            setError("No valid files found. Use: SKU_color_angle_1.jpg");
            e.target.value = "";
            return;
          }
          finalizeStructured(payload);
        })
        .catch(() => {
          setError("ZIP parse failed. Please upload a valid ZIP.");
          e.target.value = "";
        });
      return;
    }

    if (emitHostedUrls) {
      const base = hostedBasePath.endsWith("/") ? hostedBasePath : `${hostedBasePath}/`;
      const urls = mergedFiles.map((file) => `${base}${sanitizeFileName(file.name)}`);
      finalize(urls);
      return;
    }

    Promise.all(
      mergedFiles.map((file) => {
        return new Promise((resolve) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.readAsDataURL(file);
        });
      })
    ).then(finalize);
  };

  const removeImage = (index) => {
    const updated = toPreviewArray(preview).filter((_, i) => i !== index);
    setPreview(updated);
    onChange(updated);
  };

  return (
    <div>
      <input type="file" multiple={multiple} accept={accept} onChange={handleFiles} />
      {maxFileSizeMB ? (
        <div style={{ marginTop: 6, fontSize: 12, color: "#7a5e45" }}>
          Max {maxFileSizeMB} MB per file{maxFiles ? `, up to ${maxFiles} files` : ""}.
        </div>
      ) : null}
      {emitHostedUrls ? (
        <div style={{ marginTop: 4, fontSize: 12, color: "#7a5e45" }}>
          Using hosted URL mode ({hostedBasePath}).
        </div>
      ) : null}
      {parseZipByName ? (
        <div style={{ marginTop: 4, fontSize: 12, color: "#7a5e45" }}>
          ZIP mode: filename format `SKU_color_angle_index.jpg` (example: SKU123_beige_front_1.jpg)
        </div>
      ) : null}
      {error ? (
        <div style={{ marginTop: 6, fontSize: 12, color: "#c0392b" }}>{error}</div>
      ) : null}

      <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
        {toPreviewArray(preview).map((img, i) => (
          <div key={i} style={{ position: "relative" }}>
            <img
              src={img}
              alt=""
              width={80}
              height={80}
              style={{ objectFit: "cover", borderRadius: 6 }}
            />
            <button
              onClick={() => removeImage(i)}
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                background: "red",
                color: "#fff",
                border: "none"
              }}
            >
              ×
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}